import { ILanguages, ITranslations } from '@llws/lift-entity-interfaces';
import { getNewTranslation } from '@/shared/factory/new-translation';
import { IObjectWithTranslations } from '@/shared';
import { INewTranslationSettings } from '@/shared/interfaces/new-translation-settings';

export const formatTranslations = (
  translations: ITranslations[],
  language: ILanguages,
  foreignId: number,
  newTranslationSettings?: INewTranslationSettings,
): ITranslations[] => {
  const formatted = translations.filter(
    (t) => t.foreignId === foreignId
      && t.languageId === language.id
      && (newTranslationSettings?.keys || [t.key]).includes(t.key),
  );

  if (newTranslationSettings) {
    newTranslationSettings.keys.forEach((k) => {
      if (!formatted.some((t) => t.key === k)) {
        formatted.push(getNewTranslation(
          newTranslationSettings.clientId,
          foreignId,
          language.id!,
          k,
          newTranslationSettings.model,
          newTranslationSettings.relatedId,
        ));
      }
    });
  }

  return formatted;
};

/**
 * Adds translations to the passed `data` using the passed `translations` and `languages`.
 * The resultant objects receive a `languages` array containing a copy of each language in `languages`, alongside any matching translations in the `translations` array of each `language`.
 *
 * If `newTranslationSettings` interface is passed, then empty translation records (id is undefined) are generated for each object in `data`.
 * @param data - The data to match translations to.
 * @param translations - The translations to map to `datra`
 * @param languages - The languages we add to each data object. For each language, translations will be mapped according to which language the translation belongs to.
 * @param newTranslationSettings - OPTIONAL. When provided, will add empty translations records for each defined translatable key in the interface's `keys` string array.
 */
export const translationFormatter = <DataObjectType>(
  data: DataObjectType | DataObjectType[],
  translations: ITranslations[],
  languages: ILanguages[],
  newTranslationSettings?: INewTranslationSettings,
): IObjectWithTranslations<DataObjectType>[] | IObjectWithTranslations<DataObjectType> => {
  const dataToModify: DataObjectType[] = Array.isArray(data) ? data : [data];
  const returnData = dataToModify.map((d) => ({
    ...d,
    ...{
      languages: languages.map((l: ILanguages) => ({
        ...l,
        ...{ translations: formatTranslations(translations, l, (d as any).id, newTranslationSettings) },
      })),
    },
  })) as IObjectWithTranslations<DataObjectType>[];

  return Array.isArray(data) ? returnData : returnData[0];
};
